import React, { useState, useEffect, useCallback } from 'react';
import { 
  Wallet, Calendar, Users, TrendingUp, Banknote, CreditCard,
  RefreshCw, Download, Check, Clock, AlertCircle, ChevronDown,
  Search, Filter, X, History, User, Car, Receipt, ArrowRight,
  CheckCircle2, XCircle, DollarSign, FileText, Eye, Printer
} from 'lucide-react';
import api from '../../services/api';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { sq } from 'date-fns/locale/sq';
import { toast } from 'sonner';

const DailyClosingPage = () => {
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);
  const [collectionHistory, setCollectionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [viewMode, setViewMode] = useState('daily'); // daily, monthly, history
  const [driverFilter, setDriverFilter] = useState('');
  
  // Collection Modal
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [collectAmount, setCollectAmount] = useState('');
  const [collectNotes, setCollectNotes] = useState('');
  const [collecting, setCollecting] = useState(false);

  const fetchRevenueData = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = viewMode === 'daily' 
        ? { date: selectedDate }
        : { month: selectedMonth };
      
      const response = await api.get('/daily-closing/revenue', { params });
      setRevenueData(response.data);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      toast.error(error.response?.data?.detail || 'Gabim në ngarkimin e xhiros');
      setRevenueData({
        period: { date: selectedDate, month: selectedMonth },
        drivers: [],
        totals: { total_rides: 0, total_cash: 0, total_card: 0, collected_cash: 0, pending_cash: 0, drivers_with_pending: 0 }
      });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedMonth, viewMode]);

  const fetchCollectionHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      
      const now = new Date();
      const startDate = format(startOfMonth(now), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(now), 'yyyy-MM-dd');
      
      const response = await api.get('/daily-closing/collection-history', {
        params: { start_date: startDate, end_date: endDate, limit: 200 }
      });
      setCollectionHistory(response.data.collections || []);
    } catch (error) {
      console.error('Error fetching collection history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === 'history') {
      fetchCollectionHistory();
    } else {
      fetchRevenueData();
    }
  }, [fetchRevenueData, fetchCollectionHistory, viewMode]);

  const handleCollect = async () => {
    if (!selectedDriver || !collectAmount) {
      toast.error('Plotëso të gjitha fushat');
      return;
    }

    const amount = parseFloat(collectAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Shuma duhet të jetë pozitive');
      return;
    }

    setCollecting(true);
    try {
      await api.post('/daily-closing/collect', null, {
        params: {
          driver_id: selectedDriver.driver_id,
          amount_lek: amount,
          rides_count: selectedDriver.total_rides,
          notes: collectNotes || `Mbledhje cash për ${selectedDate}`
        }
      });
      
      toast.success(`U mblodhën ${formatPrice(amount)} LEK nga ${selectedDriver.driver_name}`);
      setShowCollectModal(false);
      setSelectedDriver(null);
      setCollectAmount('');
      setCollectNotes('');
      fetchRevenueData();
    } catch (error) {
      console.error('Error collecting cash:', error);
      toast.error(error.response?.data?.detail || 'Gabim gjatë mbledhjes');
    } finally {
      setCollecting(false);
    }
  };

  const openCollectModal = (driver) => {
    setSelectedDriver(driver);
    setCollectAmount(driver.pending_cash > 0 ? driver.pending_cash.toString() : '');
    setCollectNotes('');
    setShowCollectModal(true);
  };

  const formatPrice = (amount) => {
    return Number(amount || 0).toLocaleString('sq-AL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '--';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy, HH:mm', { locale: sq });
    } catch {
      return dateStr;
    }
  };

  const filteredDrivers = revenueData?.drivers?.filter(driver => {
    if (!driverFilter) return true;
    const search = driverFilter.toLowerCase();
    return (
      driver.driver_name?.toLowerCase().includes(search) ||
      driver.vehicle_plate?.toLowerCase().includes(search)
    );
  }) || [];

  const driversWithPending = filteredDrivers.filter(d => d.pending_cash > 0);

  return (
    <div className="p-6 space-y-6" data-testid="daily-closing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mbyllja Ditore</h1>
          <p className="text-gray-500 text-sm">Mbledhja e cash-it dhe xhiro ditore</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => viewMode === 'history' ? fetchCollectionHistory() : fetchRevenueData()}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading || historyLoading ? 'animate-spin' : ''}`} />
            Rifresko
          </button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setViewMode('daily')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition ${
            viewMode === 'daily' 
              ? 'border-red-600 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Calendar className="w-4 h-4 inline mr-2" />
          Ditore
        </button>
        <button
          onClick={() => setViewMode('monthly')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition ${
            viewMode === 'monthly' 
              ? 'border-red-600 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <TrendingUp className="w-4 h-4 inline mr-2" />
          Mujore
        </button>
        <button
          onClick={() => setViewMode('history')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition ${
            viewMode === 'history' 
              ? 'border-red-600 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <History className="w-4 h-4 inline mr-2" />
          Historiku
        </button>
      </div>

      {/* Date/Month Selector */}
      {viewMode !== 'history' && (
        <div className="flex items-center gap-4">
          {viewMode === 'daily' ? (
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
          )}
          
          <div className="flex-1 relative max-w-xs">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Kërko shofer..."
              value={driverFilter}
              onChange={(e) => setDriverFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {viewMode !== 'history' && revenueData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Car className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-gray-500 text-sm">Udhëtime</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{revenueData.totals?.total_rides || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Banknote className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-gray-500 text-sm">Cash Total</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatPrice(revenueData.totals?.total_cash)} <span className="text-sm font-normal">LEK</span></p>
          </div>

          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-gray-500 text-sm">Kartë Total</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatPrice(revenueData.totals?.total_card)} <span className="text-sm font-normal">LEK</span></p>
          </div>

          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-gray-500 text-sm">Mbledhur</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{formatPrice(revenueData.totals?.collected_cash)} <span className="text-sm font-normal">LEK</span></p>
          </div>

          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-gray-500 text-sm">Për tu Mbledhur</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{formatPrice(revenueData.totals?.pending_cash)} <span className="text-sm font-normal">LEK</span></p>
            {revenueData.totals?.drivers_with_pending > 0 && (
              <p className="text-xs text-orange-500 mt-1">{revenueData.totals.drivers_with_pending} shoferë</p>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : viewMode === 'history' ? (
        /* Collection History View */
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Historiku i Mbledhjeve</h3>
            <p className="text-sm text-gray-500">Këtë muaj</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Shoferi</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Shuma</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Mbledhur nga</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Shënim</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {historyLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Duke ngarkuar...
                    </td>
                  </tr>
                ) : collectionHistory.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      Nuk ka mbledhje këtë muaj
                    </td>
                  </tr>
                ) : (
                  collectionHistory.map((collection) => (
                    <tr key={collection.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatDateTime(collection.collection_date)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{collection.driver_name}</p>
                            <p className="text-xs text-gray-500">{collection.vehicle_plate}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-green-600">
                          {formatPrice(collection.amount_lek)} LEK
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {collection.collected_by_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {collection.notes || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Drivers Revenue Table */
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Xhiro sipas Shoferit</h3>
              <p className="text-sm text-gray-500">
                {viewMode === 'daily' 
                  ? format(parseISO(selectedDate), 'dd MMMM yyyy', { locale: sq })
                  : format(parseISO(selectedMonth + '-01'), 'MMMM yyyy', { locale: sq })
                }
              </p>
            </div>
            {driversWithPending.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-lg border border-orange-200">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                <span className="text-sm text-orange-700 font-medium">
                  {driversWithPending.length} shoferë me cash pending
                </span>
              </div>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Shoferi</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Udhëtime</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Cash</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Kartë</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Mbledhur</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Pending</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Veprime</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredDrivers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      {driverFilter ? 'Nuk u gjet asnjë shofer' : 'Nuk ka të dhëna për këtë periudhë'}
                    </td>
                  </tr>
                ) : (
                  filteredDrivers.map((driver) => (
                    <tr 
                      key={driver.driver_id} 
                      className={`hover:bg-gray-50 ${driver.pending_cash > 0 ? 'bg-orange-50/50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-medium">
                            {driver.driver_name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{driver.driver_name}</p>
                            <p className="text-xs text-gray-500">{driver.vehicle_plate || 'Pa targë'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {driver.total_rides}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium text-gray-900">{formatPrice(driver.total_cash)}</span>
                        <span className="text-gray-400 text-xs ml-1">LEK</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-gray-600">{formatPrice(driver.total_card)}</span>
                        <span className="text-gray-400 text-xs ml-1">LEK</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-emerald-600 font-medium">{formatPrice(driver.collected_cash)}</span>
                        <span className="text-gray-400 text-xs ml-1">LEK</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {driver.pending_cash > 0 ? (
                          <span className="text-orange-600 font-bold">{formatPrice(driver.pending_cash)} LEK</span>
                        ) : driver.pending_cash < 0 ? (
                          <span className="text-red-600">{formatPrice(driver.pending_cash)} LEK</span>
                        ) : (
                          <span className="text-emerald-600 flex items-center justify-end gap-1">
                            <CheckCircle2 className="w-4 h-4" />
                            OK
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {driver.pending_cash > 0 && (
                          <button
                            onClick={() => openCollectModal(driver)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition"
                          >
                            <Wallet className="w-3.5 h-3.5" />
                            Mbledh
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Collection Modal */}
      {showCollectModal && selectedDriver && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Mbledh Cash</h3>
              <button 
                onClick={() => setShowCollectModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Driver Info */}
            <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white text-xl font-bold">
                {selectedDriver.driver_name?.charAt(0) || '?'}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{selectedDriver.driver_name}</p>
                <p className="text-sm text-gray-500">{selectedDriver.vehicle_plate}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    {selectedDriver.total_rides} udhëtime
                  </span>
                </div>
              </div>
            </div>

            {/* Amount Summary */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                <p className="text-xs text-green-600 mb-1">Cash Total</p>
                <p className="text-lg font-bold text-green-700">{formatPrice(selectedDriver.total_cash)} LEK</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                <p className="text-xs text-orange-600 mb-1">Pending</p>
                <p className="text-lg font-bold text-orange-700">{formatPrice(selectedDriver.pending_cash)} LEK</p>
              </div>
            </div>

            {/* Collection Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shuma për të mbledhur</label>
                <div className="relative">
                  <input
                    type="number"
                    value={collectAmount}
                    onChange={(e) => setCollectAmount(e.target.value)}
                    placeholder="0"
                    className="w-full px-4 py-3 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-lg font-semibold"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">LEK</span>
                </div>
                {selectedDriver.pending_cash > 0 && parseFloat(collectAmount) !== selectedDriver.pending_cash && (
                  <button
                    onClick={() => setCollectAmount(selectedDriver.pending_cash.toString())}
                    className="text-xs text-red-600 hover:text-red-700 mt-1"
                  >
                    Vendos shumën e plotë ({formatPrice(selectedDriver.pending_cash)} LEK)
                  </button>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shënim (opsional)</label>
                <textarea
                  value={collectNotes}
                  onChange={(e) => setCollectNotes(e.target.value)}
                  placeholder="P.sh. Mbledhje në fund të turnit"
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCollectModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
              >
                Anulo
              </button>
              <button
                onClick={handleCollect}
                disabled={collecting || !collectAmount}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-red-400 transition flex items-center justify-center gap-2"
              >
                {collecting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Duke mbledhur...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Konfirmo Mbledhjen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyClosingPage;
